import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, Copy, X, Check } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";

// Editable campaign config — change here for the next campaign.
const CAMPAIGN = {
  code: "ADVENT15",
  copy: {
    de: { eyebrow: "ADVENT-AKTION", body: "15% Rabatt auf alle Honige", cta: "Code kopieren", clicked: "Kopiert! Beim Checkout einlösen", link: "Jetzt bestellen" },
    en: { eyebrow: "ADVENT OFFER", body: "15% off all honeys", cta: "Copy code", clicked: "Copied! Redeem at checkout", link: "Shop now" },
    it: { eyebrow: "OFFERTA AVVENTO", body: "15% di sconto su tutti i mieli", cta: "Copia codice", clicked: "Copiato! Utilizza al checkout", link: "Ordina ora" },
    ja: { eyebrow: "アドベント・オファー", body: "すべてのはちみつが15%オフ", cta: "コードをコピー", clicked: "コピーしました!チェックアウトでご利用ください", link: "今すぐ購入" },
    zh: { eyebrow: "降临节特惠", body: "全场蜂蜜85折", cta: "复制优惠码", clicked: "已复制!结账时使用", link: "立即选购" },
  },
};

const STORAGE_KEY = "madubau_campaign_dismissed_v1";

export default function CampaignBanner() {
  const { lang } = useI18n();
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const L = CAMPAIGN.copy[lang] || CAMPAIGN.copy.de;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed !== CAMPAIGN.code) setVisible(true);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, CAMPAIGN.code);
    setVisible(false);
  };

  const copy = () => {
    navigator.clipboard.writeText(CAMPAIGN.code);
    setCopied(true);
    toast.success(L.clicked);
    setTimeout(() => setCopied(false), 2600);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 60, opacity: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
          className="fixed bottom-0 left-0 right-0 z-[140] bg-gradient-to-r from-[#0d0d0d] via-[#191207] to-[#0d0d0d] border-t border-gold/40 shadow-[0_-20px_60px_rgba(0,0,0,0.6)]"
          data-testid="campaign-banner"
          role="region"
          aria-label="Promotion banner"
        >
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-3 flex items-center justify-center gap-4 flex-wrap text-[10px] sm:text-[11px] font-display tracking-[0.28em] relative pr-10">
            <Sparkles className="w-3 h-3 text-gold shrink-0 hidden sm:block" />
            <span className="text-gold hidden sm:inline">/ {L.eyebrow}</span>
            <span className="text-cream/85">
              <span className="hidden sm:inline">— </span>
              {L.body}
            </span>
            <button
              type="button"
              onClick={copy}
              data-testid="campaign-copy"
              className="inline-flex items-center gap-1.5 border border-gold/50 text-gold hover:bg-gold hover:text-black px-3 py-1.5 transition-colors"
              aria-label="Copy discount code"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              <span className="tracking-[0.35em]">{CAMPAIGN.code}</span>
            </button>
            <Link
              to="/shop"
              data-testid="campaign-shop-link"
              className="hidden md:inline-flex text-cream/80 hover:text-gold link-underline"
            >
              → {L.link}
            </Link>
            <button
              type="button"
              onClick={dismiss}
              data-testid="campaign-dismiss"
              aria-label="Dismiss banner"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-cream/40 hover:text-gold"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
